import { AxiosError } from 'axios';
import { FormikHelpers } from 'formik/dist/types';
import { useCallback } from 'react';
import { useHistory } from 'react-router-dom';

import useTriggeredRequest from '@api/hooks/useTriggeredRequest';
import AccountClientRequests from '@api/requests';

import RoutePaths from '@app/routes/paths';
import useGenerateRoutePath from '@app/routes/paths/useGenerateRoutePath';

import { RegistrationFormValues } from '@components/forms/registration/interfaces';

function useRegistration({ validatedEmail }: { validatedEmail?: string } = {}) {
	const history = useHistory();
	const generateRoutePath = useGenerateRoutePath();

	const { startRequest } = useTriggeredRequest(AccountClientRequests.Auth.registration);

	const initialValues: RegistrationFormValues = {
		first_name: '',
		last_name: '',
		email: validatedEmail ?? '',
		password: '',
		password_confirmation: '',
		phone: {
			code: '+1',
			number: '',
		},
		terms: false,
	};

	const onSubmit = useCallback(
		async (values: RegistrationFormValues, formikHelpers: FormikHelpers<RegistrationFormValues>) => {
			try {
				await startRequest({
					first_name: values.first_name,
					last_name: values.last_name,
					email: values.email,
					password: values.password,
					phone: values.phone.code + values.phone.number,
				});

				history.push(generateRoutePath(RoutePaths.AUTH.LOGIN));
			} catch (e: any) {
				const { response } = e as AxiosError<{
					messages: {
						first_name: string[];
						last_name: string[];
						email: string[];
						password: string[];
						phone: string[];
					};
				}>;

				const errors = e.response?.data?.errors;
				if (errors) {
					for (const error of errors) {
						if (error.code === 4090 || error.code === 4091) {
							history.push(generateRoutePath(RoutePaths.AUTH.USER_EXISTS));
							return;
						}
					}
				}

				formikHelpers.setErrors({
					first_name: response?.data?.messages?.first_name?.join(' ') || '',
					last_name: response?.data?.messages?.last_name?.join(' ') || '',
					email: response?.data?.messages?.email?.join(' ') || '',
					password: response?.data?.messages?.password?.join(' ') || '',
					phone: {
						code: '',
						number: response?.data?.messages?.phone?.join(' ') || '',
					},
				});
			}
		},
		[generateRoutePath, history, startRequest],
	);

	return {
		initialValues,
		onSubmit,
	};
}

export default useRegistration;
